import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { EditorWorkflow, EditorWorkflowDialogComponent } from '@fry/booking/workflow-editor';

import { EditorBookingItem, SETTING_WORKFLOW_INDEX } from '../editor-booking-item/editor-booking-item.model';
import { EditorBookingItemSettingWorkflow } from '../editor-booking-item-setting/editor-booking-item-setting-workflow.model';
import { EditorBookingItemTreeStore } from '../editor-booking-item-tree/editor-booking-item-tree.store';


@Component({
    selector: 'eas-editor-workflow-editor-loader',
    templateUrl: './editor-workflow-editor-loader.component.html',
    styleUrls: ['./editor-workflow-editor-loader.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditorWorkflowEditorLoaderComponent implements OnInit, OnDestroy {
  private destroyed$ = new Subject<void>();

  @Input() workflowId: string;

  @Output() set = new EventEmitter<{ id: string }>();
  @Output() unset = new EventEmitter<void>();

  itemList: EditorBookingItem[];
  workflowList: EditorWorkflow[];

  constructor(
    private dialog: MatDialog,
    private itemTreeStore: EditorBookingItemTreeStore
  ) {

  }

  ngOnInit() {
    this.itemTreeStore.bookingItem$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(bookingItem => this.workflowList = bookingItem.doc.workflows);

    this.itemTreeStore.itemTree$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(itemTree => this.itemList = itemTree.itemList);
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private openWorkflowEditor(action: 'create'|'edit'|'duplicate', workflowId: string) {
    const data = {
      action,
      workflow: action === 'create'
        ? undefined
        : this.workflowList.find(workflow => workflow.id === workflowId),
      assignedBookingItemNameList: action === 'edit'
        ? this.itemList
          .filter(
            item => (item.settings[SETTING_WORKFLOW_INDEX] as EditorBookingItemSettingWorkflow).id === this.workflowId
          ).map(
            item => item.name
          )
        : []
    };

    const dialogRef = this.dialog.open(EditorWorkflowDialogComponent, {
      closeOnNavigation: true,
      disableClose: true,
      hasBackdrop: true,
      height: '100%',
      width: '100%',
      maxWidth: '90vw',
      panelClass: 'mat-typography',
      id: 'booking-item-editor-workflow-dialog',
      data
    });

    dialogRef.afterClosed().subscribe(workflowWithAction => {
      if (workflowWithAction === undefined) {
        return;
      }

      switch (workflowWithAction.action) {
        case 'create':
          this.itemTreeStore.addWorkflow(workflowWithAction.workflow);
          this.set.emit({ id: workflowWithAction.workflow.id });
          break;
        case 'edit':
          this.itemTreeStore.replaceWorkflow(workflowId, workflowWithAction.workflow);
          break;
        case 'delete':
          this.unset.emit();
          this.itemTreeStore.deleteWorkflow(workflowId);
          break;
      }
    });
  }

  onCreate() {
    this.openWorkflowEditor('create', undefined);
    return false;
  }

  onEdit(workflowId: string) {
    this.openWorkflowEditor('edit', workflowId);
    return false;
  }

  onDuplicate(workflowId: string) {
    this.openWorkflowEditor('duplicate', workflowId);
    return false;
  }
}
